import React from "react";
import {
  DocumentData,
  WithFieldValue,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";

import { AuthContext, FirebaseContext } from "@contexts";
import { AuthUser } from "@constants";
import { DocumentActions } from "./firestoreHookTypes";

const baseAuthUser: AuthUser = {
  firstName: "",
  lastName: "",
  role: "user",
  email: "",
  location: "Brown",
};

/**
 * A abstraction for Firestore that queries the database documents and
 * provides an API for updating / deleting them.
 * This is meant to be wrapped by a `useGet<CollectionDocument>` hook.
 * @param {string} collectionName Collection being queried.
 * @param {string} documentName Document being queried.
 * @returns {[T, DocumentActions]} Document + update / delete functions for that document.
 */
export function useGetDocument<T>(
  collectionName: string,
  documentName: string,
): [T, DocumentActions] {
  const { authUser } = React.useContext(AuthContext);
  const { firestore } = React.useContext(FirebaseContext);
  const [document, setDocument] = React.useState<DocumentData>();
  const user = React.useMemo(
    () => (authUser ? authUser : baseAuthUser),
    [authUser],
  );
  const documentActionError = React.useMemo(
    () =>
      new Error(
        `Document ${collectionName}/${documentName} hasn't been fetched yet.`,
      ),
    [collectionName, documentName],
  );

  React.useEffect(() => {
    if (!documentName || !collectionName) {
      setDocument(undefined);
      return;
    }

    // Clear previous document immediately
    setDocument(undefined);

    return onSnapshot(
      doc(firestore, collectionName, documentName),
      (docSnapshot) =>
        setDocument({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }),
      (error) => console.log({ error }),
    );
  }, [firestore, collectionName, documentName, user]);

  const updateCallback = React.useCallback(
    function doUpdate<UpdateT extends WithFieldValue<DocumentData>>(
      data: UpdateT,
    ) {
      if (!document) throw documentActionError;
      const documentReference = doc(firestore, collectionName, document.id);
      if (!documentReference) throw documentActionError;

      return updateDoc(documentReference, data);
    },
    [document, collectionName, documentName, user, documentActionError],
  );

  const deleteCallback = React.useCallback(
    function doDelete() {
      if (!document) throw documentActionError;
      const documentReference = doc(firestore, collectionName, document.id);
      if (!documentReference) throw documentActionError;

      return deleteDoc(documentReference);
    },
    [document, collectionName, documentName, user, documentActionError],
  );

  return [
    document as T,
    {
      update: updateCallback,
      delete: deleteCallback,
    },
  ];
}
